import { typedEnv } from "../config/typedEnv"

export type PlatformServiceName = "admin" | "billing" | "viewer3dAdmin"

// viewer3dAdmin is the extracted 3D admin service (admin decomposition,
// release A): it serves the same /api/v{1,2}/admin path shape as admin on its
// own gateway host. The v2 demo-key mint exists only there.
const PLATFORM_SERVICE_PATH_PREFIX: Record<PlatformServiceName, string> = {
  admin: "/api/v1/admin",
  billing: "/api/v1/billing",
  viewer3dAdmin: "/api/v1/admin",
}

const PLATFORM_SERVICE_ENV_KEYS: Record<PlatformServiceName, string> = {
  admin: "VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT",
  billing: "VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT",
  viewer3dAdmin: "VITE_PLATFORM_VIEWER3D_ADMIN_TINTVTO_SERVICE_ENDPOINT",
}

export const resolveServiceEndpoint = (value: string): string => {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/\/$/, "")

  return `https://${trimmed.replace(/\/$/, "")}`
}

export const resolvePlatformServiceBaseEndpoint = (
  serviceName: PlatformServiceName,
  serviceUrl: string
): string => {
  const normalizedServiceUrl = resolveServiceEndpoint(serviceUrl)
  const servicePathPrefix = PLATFORM_SERVICE_PATH_PREFIX[serviceName]
  const url = new URL(normalizedServiceUrl)

  if (!url.pathname.endsWith(servicePathPrefix))
    throw new Error(
      `Invalid ${serviceName} platform service URL: expected it to end with "${servicePathPrefix}".`
    )

  const basePath = url.pathname.slice(0, -servicePathPrefix.length) || "/"
  url.pathname = basePath
  url.search = ""
  url.hash = ""

  return url.toString().replace(/\/$/, "")
}

const getPlatformServiceUrl = (serviceName: PlatformServiceName): string | undefined => {
  if (serviceName === "admin") return typedEnv.VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT
  if (serviceName === "viewer3dAdmin")
    return typedEnv.VITE_PLATFORM_VIEWER3D_ADMIN_TINTVTO_SERVICE_ENDPOINT
  return typedEnv.VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT
}

const requirePlatformServiceUrl = (serviceName: PlatformServiceName): string => {
  const serviceUrl = getPlatformServiceUrl(serviceName)
  if (serviceUrl) return serviceUrl

  throw new Error(
    `Missing ${PLATFORM_SERVICE_ENV_KEYS[serviceName]}. Configure the ${serviceName} platform service endpoint before making ${serviceName} API requests.`
  )
}

export const getPlatformServiceEndpoint = (
  serviceName: PlatformServiceName,
  apiVersion: "v1" | "v2" = "v1"
): string => {
  const configuredEndpoint = requirePlatformServiceUrl(serviceName)
  if (apiVersion === "v1") return resolveServiceEndpoint(configuredEndpoint)

  const baseEndpoint = resolvePlatformServiceBaseEndpoint(serviceName, configuredEndpoint)
  return `${baseEndpoint}${PLATFORM_SERVICE_PATH_PREFIX[serviceName].replace("/v1/", "/v2/")}`
}

const isLocalEndpoint = (endpoint: string): boolean => {
  const host = new URL(endpoint).hostname
  return host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0"
}

export const validatePlatformServiceEndpoints = () => {
  const serviceNames = Object.keys(PLATFORM_SERVICE_ENV_KEYS) as PlatformServiceName[]
  const baseEndpoints = serviceNames.map(
    (serviceName) =>
      [
        serviceName,
        resolvePlatformServiceBaseEndpoint(serviceName, requirePlatformServiceUrl(serviceName)),
      ] as const
  )

  for (let i = 0; i < baseEndpoints.length; i += 1)
    for (let j = i + 1; j < baseEndpoints.length; j += 1)
      if (baseEndpoints[i][1] === baseEndpoints[j][1])
        throw new Error(
          `Invalid platform service configuration: ${baseEndpoints[i][0]} and ${baseEndpoints[j][0]} resolve to the same base endpoint. Check the deployed ServiceEndpoint outputs, rerender the .env.<mode>.local file, and rebuild.`
        )

  // Production Vite builds (devstand/production, or an accidentally built
  // test mode) must never ship the committed
  // localhost placeholders from .env.devstand — that means the deploy-time
  // env render (.env.devstand.local from live CFN outputs) did not run and
  // every platform request would silently die against localhost. Vite's local
  // dev server is allowed to use localhost in every mode, including the
  // public Playwright suite's intentional `--mode test` configuration.
  if (import.meta.env.PROD)
    for (const [serviceName, endpoint] of baseEndpoints)
      if (isLocalEndpoint(endpoint))
        throw new Error(
          `Invalid ${serviceName} platform service endpoint "${endpoint}": a deployed build is pointing at localhost — the env render step (scripts/render_tint_platform_env.sh) did not run before this build.`
        )
}
